import {
  MAX_OPENER_BRAND_NAME_LENGTH,
  MAX_SVG_LOGO_BYTES,
  resolveOpenerTheme,
} from "@loot-os/api-contract";
import type { LootTheme } from "@loot-os/react";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { OpenerStyleSelect } from "../components/OpenerStyleSelect";
import { OpenerStylesPanel } from "../components/OpenerStylesPanel";
import { SettingRow } from "../components/SettingRow";
import { StorefrontSection } from "../components/StorefrontSection";
import { SvgLogoDropzone } from "../components/SvgLogoDropzone";
import {
  environmentOpenerDefaults,
  openerDefaultBadges,
  sharedOpenerDefault,
} from "../lib/openerDefaults";
import { useOpenerStyleWrites } from "../lib/openerStyles";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Openers: which opener style this environment's boxes open with (inheriting the platform
 * defaults, the storefront's and Shopify's, until an editor picks one), the logo every opener here wears as the tenant's brand
 * (an SVG, stored as validated source like the currency icon) and the name it prints in the
 * logo's place when there is none, and the environment's own styles. A top-level page, not a
 * storefront section: a box opens with its style on the storefront, in the Shopify overlay and
 * through the API alike, so it stays reachable with the storefront module off. A box or a
 * Shopify shop may still name its own. A style is created and edited on a page of its own
 * (`OpenerStylePage`).
 */
export function OpenersPage() {
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const canEdit = can("editor");
  const { query: config } = useStorefrontConfigQuery();
  const options = trpc.openerStyles.list.queryOptions();
  const query = useQuery(options);
  // The preview shows a style over the storefront theme, as the style editor does.
  const storedTheme = config.data?.config.theme;
  const hostTheme = useMemo(
    (): LootTheme | undefined => resolveOpenerTheme(storedTheme),
    [storedTheme],
  );
  const refresh = () => void queryClient.invalidateQueries({ queryKey: options.queryKey });

  const { remove } = useOpenerStyleWrites("environment");
  const setLogo = useMutation(
    trpc.openerStyles.setLogo.mutationOptions({
      onSuccess: (saved) => {
        refresh();
        toast.success(saved.logoSvg ? "Opener logo saved." : "Opener logo removed.");
      },
    }),
  );
  const setBrandName = useMutation(
    trpc.openerStyles.setBrandName.mutationOptions({
      onSuccess: (saved) => {
        refresh();
        toast.success(saved.brandName ? "Opener name saved." : "Opener name removed.");
      },
    }),
  );
  const setDefault = useMutation(
    trpc.openerStyles.setEnvironmentDefault.mutationOptions({
      onSuccess: (saved) => {
        refresh();
        toast.success(
          saved.environmentStyleId
            ? "Default opener style saved."
            : "Back on the platform default.",
        );
      },
    }),
  );

  // No StorefrontLayout above this page, so it waits on the config itself, like Shipping.
  if (config.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (config.error) return <ErrorAlert message={config.error.message} />;
  if (!config.data) return null;
  const { environment } = config.data;
  const data = query.data;
  // One inherited name only when the platform's storefront and Shopify defaults agree.
  const platformDefaults = data && {
    storefront: data.platformDefaultStyleId,
    shopify: data.platformShopifyDefaultStyleId,
  };
  const platformShared = platformDefaults && sharedOpenerDefault(platformDefaults);
  const styleName = (styleId: string) =>
    data?.styles.find((style) => style.id === styleId)?.name ?? styleId;

  return (
    <StorefrontSection
      title="Openers"
      description="How this environment's boxes open, on the storefront, in the Shopify overlay and through the API: the animation, and any colors that differ from the host's theme. A box, or the Shopify shop, can pick its own."
      environment={environment}
    >
      {query.error && <ErrorAlert message={query.error.message} />}
      {!data && !query.error && <div className="text-sm text-muted-foreground">Loading…</div>}
      {data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Defaults</CardTitle>
              <CardDescription>
                What every box here opens with unless it names its own style, and the brand every
                opener wears whichever style that is.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid divide-y">
              <SettingRow
                htmlFor="environment-opener-style"
                label="Default style"
                description={
                  platformDefaults && platformShared === null
                    ? `Boxes that name no style of their own open with this one. The platform defaults are ${styleName(platformDefaults.storefront)} on the storefront and ${styleName(platformDefaults.shopify)} in Shopify.`
                    : "Boxes that name no style of their own open with this one."
                }
              >
                <OpenerStyleSelect
                  id="environment-opener-style"
                  styles={data.styles}
                  value={data.environmentStyleId}
                  inheritLabel={platformShared === null ? "Platform defaults" : "Platform default"}
                  inheritedStyleId={platformShared}
                  disabled={!canEdit || setDefault.isPending}
                  onChange={(styleId) => setDefault.mutate({ styleId })}
                />
              </SettingRow>
              <SettingRow
                htmlFor="opener-brand-name"
                label="Brand name"
                description="Printed as text where the logo goes, when there is no logo."
              >
                <BrandNameField
                  // Remounted on each save, so the field starts from what was stored.
                  key={data.brandName}
                  value={data.brandName}
                  disabled={!canEdit || setBrandName.isPending}
                  onSave={(name) => setBrandName.mutate({ name })}
                />
              </SettingRow>
              <SettingRow
                htmlFor="opener-logo"
                label="Logo"
                description="Shown on every opener in place of its stock emblem. Wins over the name when both are set."
              >
                <div className="max-w-md">
                  <SvgLogoDropzone
                    id="opener-logo"
                    value={data.logoSvg}
                    onChange={(svg) => setLogo.mutate({ svg })}
                    help={`SVG up to ${Math.floor(MAX_SVG_LOGO_BYTES / 1024)} KB, text as outlines, no scripts or linked images.`}
                    maxBytes={MAX_SVG_LOGO_BYTES}
                    disabled={!canEdit || setLogo.isPending}
                  />
                </div>
              </SettingRow>
            </CardContent>
          </Card>
          <section className="grid gap-4">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Styles</h2>
              <p className="text-sm text-muted-foreground">
                The platform's styles, and this environment's own. Colors left blank follow the
                storefront theme.
              </p>
            </div>
            <OpenerStylesPanel
              styles={data.styles}
              editableScope="environment"
              hostTheme={hostTheme}
              logoSvg={data.logoSvg}
              brandName={data.brandName}
              defaults={openerDefaultBadges(environmentOpenerDefaults(data))}
              canEdit={canEdit}
              editLink={(styleId) => (
                <Link
                  to="/tenants/$tenantId/openers/$styleId"
                  params={{ tenantId, styleId }}
                  className={buttonVariants({ size: "sm", variant: "outline" })}
                >
                  Edit
                </Link>
              )}
              newLink={
                <Link
                  to="/tenants/$tenantId/openers/new"
                  params={{ tenantId }}
                  className={buttonVariants()}
                >
                  New style
                </Link>
              }
              onDelete={(styleId) => remove.mutate({ styleId })}
            />
          </section>
        </>
      )}
    </StorefrontSection>
  );
}

/** The opener brand name, saved on Enter or with its Save button once it differs from `value`. */
function BrandNameField({
  value,
  disabled,
  onSave,
}: {
  value: string;
  disabled: boolean;
  onSave: (name: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const dirty = draft.trim() !== value;
  return (
    <form
      className="flex gap-2 sm:max-w-80"
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty) onSave(draft.trim());
      }}
    >
      <Input
        id="opener-brand-name"
        value={draft}
        maxLength={MAX_OPENER_BRAND_NAME_LENGTH}
        placeholder="Your store's name"
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
      />
      {dirty && (
        <Button type="submit" variant="outline" disabled={disabled}>
          Save
        </Button>
      )}
    </form>
  );
}
